import { getApiEndpoint } from '@/config/api';
import { authService } from '@/services/authService';
import { apiJson, apiJsonArray } from '@/lib/api-client';

export interface VoucherAudit {
  id: number;
  voucher_id: number;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | 'ACTIVATE' | 'DEACTIVATE';
  old_values: unknown;
  new_values: unknown;
  changed_by: number | null;
  changed_at: string;
  ip_address: string | null;
  user_agent: string | null;
  changed_by_name?: string;
}

export interface Voucher {
  id: number;
  partner_id: number;
  code: string;
  title: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  usage_limit: number | null;
  usage_count: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  partner_name?: string;
  partner_logo?: string;
}

export class VoucherService {
  static generateVoucherCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 16; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  static async getVouchersByPartner(partnerId: number): Promise<Voucher[]> {
    return apiJsonArray<Voucher>(`api/vouchers/partner/${partnerId}`);
  }

  static async getVoucherById(id: number): Promise<Voucher | null> {
    return apiJson<Voucher | null>(`api/vouchers/${id}`);
  }

  static async getVoucherByCode(code: string): Promise<Voucher | null> {
    return apiJson<Voucher | null>(
      `api/vouchers/code/${encodeURIComponent(code)}`,
    );
  }

  private static formatDateForMySQL(
    dateString: string | null | undefined,
  ): string | null {
    if (dateString === null || dateString === undefined) return null;
    const str = String(dateString).trim();
    if (str === '' || str === 'null' || str === 'undefined') return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const dateMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${year}-${month}-${day}`;
    }
    return null;
  }

  static async createVoucher(
    voucherData: Omit<
      Voucher,
      'id' | 'code' | 'usage_count' | 'created_at' | 'updated_at'
    > & { start_date?: string | null; end_date?: string | null },
    _changedBy: number | null = null,
  ): Promise<number> {
    const v = await apiJson<Voucher>('api/vouchers', {
      method: 'POST',
      body: JSON.stringify({
        partner_id: voucherData.partner_id,
        title: voucherData.title,
        description: voucherData.description,
        discount_type: voucherData.discount_type,
        discount_value: voucherData.discount_value,
        start_date: VoucherService.formatDateForMySQL(voucherData.start_date),
        end_date: VoucherService.formatDateForMySQL(voucherData.end_date),
        is_active: voucherData.is_active,
        usage_limit: voucherData.usage_limit,
      }),
    });
    return v.id;
  }

  static async updateVoucher(
    id: number,
    voucherData: Partial<
      Omit<Voucher, 'id' | 'code' | 'usage_count' | 'created_at' | 'updated_at'>
    >,
    _changedBy: number | null = null,
  ): Promise<boolean> {
    const payload: Record<string, unknown> = {};
    if (voucherData.title !== undefined) payload.title = voucherData.title;
    if (voucherData.description !== undefined) {
      payload.description = voucherData.description;
    }
    if (voucherData.discount_type !== undefined) {
      payload.discount_type = voucherData.discount_type;
    }
    if (voucherData.discount_value !== undefined) {
      payload.discount_value = voucherData.discount_value;
    }
    if (voucherData.start_date !== undefined) {
      payload.start_date = VoucherService.formatDateForMySQL(
        voucherData.start_date,
      );
    }
    if (voucherData.end_date !== undefined) {
      payload.end_date = VoucherService.formatDateForMySQL(
        voucherData.end_date,
      );
    }
    if (voucherData.is_active !== undefined) {
      payload.is_active = voucherData.is_active;
    }
    if (voucherData.usage_limit !== undefined) {
      payload.usage_limit = voucherData.usage_limit;
    }
    if (Object.keys(payload).length === 0) return true;
    await apiJson(`api/vouchers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return true;
  }

  static async deleteVoucher(
    id: number,
    _changedBy: number | null = null,
  ): Promise<boolean> {
    const r = await apiJson<{ success: boolean }>(`api/vouchers/${id}`, {
      method: 'DELETE',
    });
    return r.success;
  }

  static async incrementUsage(code: string): Promise<boolean> {
    const r = await apiJson<{ success: boolean }>(
      'api/vouchers/increment-usage',
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
    );
    return r.success;
  }

  static async softDeleteVoucher(
    voucherId: number,
    _changedBy: number | null = null,
  ): Promise<boolean> {
    return this.deleteVoucher(voucherId);
  }

  static async activateVoucher(
    voucherId: number,
    _changedBy: number | null = null,
  ): Promise<boolean> {
    return this.updateVoucher(voucherId, { is_active: true });
  }

  static async getVoucherAuditHistory(
    voucherId: number,
  ): Promise<VoucherAudit[]> {
    return apiJsonArray<VoucherAudit>(`api/vouchers/${voucherId}/audit`);
  }

  static async registerPrint(voucherId: number): Promise<void> {
    try {
      await fetch(getApiEndpoint(`/api/vouchers/${voucherId}/print`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* n�o bloquear impress�o */
    }
  }

  static async getPrintStats(): Promise<
    Array<{
      id: number;
      code: string;
      title: string;
      partner_name: string;
      print_count: number;
    }>
  > {
    const authData = authService.getAuthData();
    if (!authData.token) {
      throw new Error('Token de autentica��o n�o encontrado');
    }
    return apiJson('api/vouchers/print-stats');
  }
}
